import jsPDF from "jspdf";
import jsPDFAutoTable from "jspdf-autotable";



/**
 * Generate a beautifully formatted PDF from Zatouna summary data
 * @param {Object} summaryData - The summary data containing title, key_points, and flashcards
 * @param {string} fileName - Optional custom filename for the PDF
 * @returns {Promise<void>}
 */
export const generateZatounaPDF = async (summaryData, fileName = "zatouna-summary") => {
  try {
    // Create new PDF document (A4 size)
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Define colors matching the website theme
    const colors = {
      primary: [120, 105, 211], // #7869d3
      secondary: [90, 77, 184], // #5a4db8
      accent: [251, 191, 36], // #fbbf24
      text: [30, 41, 59], // #1e293b
      lightGray: [148, 163, 184], // #94a3b8
      white: [255, 255, 255],
    };

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 20;

    // Add gradient background effect (simulated with rectangles)
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 40, "F");
    
    // Add decorative corner elements
    doc.setFillColor(...colors.accent);
    doc.circle(10, 10, 3, "F");
    doc.circle(pageWidth - 10, 10, 3, "F");

    // Add logo/branding text
    doc.setTextColor(...colors.white);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("AI-Powered Summary", pageWidth / 2, 12, { align: "center" });

    // Add main title
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("Zatouna - Smart Summary", pageWidth / 2, 25, { align: "center" });

    // Add subtitle
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Interactive Summary Generated by AI", pageWidth / 2, 32, { align: "center" });

    yPosition = 50;

    // Add summary title section
    if (summaryData.summary_title) {
      // Title background box
      doc.setFillColor(...colors.secondary);
      doc.roundedRect(15, yPosition - 5, pageWidth - 30, 15, 3, 3, "F");

      doc.setTextColor(...colors.white);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      
      // Use English label for title
      doc.text("Summary Title:", pageWidth / 2, yPosition + 3, { align: "center" });
      
      // The original code rendered summaryData.summary_title here.
      // The instruction replaced it with a static label.
      // To maintain syntactic correctness for yPosition calculation,
      // we'll assume a single line for the static label.
      const titleLinesLength = 1; // Assuming "Summary Title:" is one line
      yPosition += 10 + (titleLinesLength * 5);
    }

    yPosition += 10;

    // Add key points section
    if (summaryData.key_points && summaryData.key_points.length > 0) {
      // Section header
      doc.setFillColor(...colors.accent);
      doc.roundedRect(15, yPosition, 8, 8, 2, 2, "F");
      
      doc.setTextColor(...colors.text);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Key Points", 28, yPosition + 6);

      yPosition += 15;

      // Add each key point
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      summaryData.key_points.forEach((point, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }

        // Point number circle
        doc.setFillColor(...colors.primary);
        doc.circle(20, yPosition - 1, 3, "F");
        doc.setTextColor(...colors.white);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text((index + 1).toString(), 20, yPosition + 1, { align: "center" });

        // Point text
        doc.setTextColor(...colors.text);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        
        const pointLines = doc.splitTextToSize(point, pageWidth - 45);
        doc.text(pointLines, 28, yPosition);
        
        yPosition += pointLines.length * 5 + 5;
      });

      yPosition += 10;
    }

    // Add flashcards section
    if (summaryData.flashcards && summaryData.flashcards.length > 0) {
      // Check if we need a new page
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }

      // Section header
      doc.setFillColor(...colors.accent);
      doc.roundedRect(15, yPosition, 8, 8, 2, 2, "F");
      
      doc.setTextColor(...colors.text);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Flashcards", 28, yPosition + 6);

      yPosition += 15;

      // Create table data for flashcards
      const tableData = summaryData.flashcards.map((card, index) => [
        (index + 1).toString(),
        card.question || "",
        card.answer || "",
      ]);

      // Add flashcards table with beautiful styling
      jsPDFAutoTable(doc, {
        startY: yPosition,
        head: [["#", "Question", "Answer"]],
        body: tableData,
        theme: "grid",
        styles: {
          font: "helvetica",
          fontSize: 10,
          cellPadding: 5,
          lineColor: colors.lightGray,
          lineWidth: 0.1,
          halign: "right",
          valign: "middle",
        },
        headStyles: {
          fillColor: colors.primary,
          textColor: colors.white,
          fontStyle: "bold",
          fontSize: 11,
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center", fillColor: [240, 240, 255] },
          1: { cellWidth: 75 },
          2: { cellWidth: 75 },
        },
        alternateRowStyles: {
          fillColor: [250, 250, 255],
        },
        margin: { left: 15, right: 15 },
      });

      yPosition = doc.lastAutoTable.finalY + 10;
    }

    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Footer background
      doc.setFillColor(...colors.primary);
      doc.rect(0, pageHeight - 15, pageWidth, 15, "F");

      // Footer text
      doc.setTextColor(...colors.white);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      
      const footerText = `Created by Zatouna - EMt7any Educational Platform`;
      doc.text(footerText, pageWidth / 2, pageHeight - 8, { align: "center" });
      
      // Page number
      doc.setFontSize(9);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 8, { align: "right" });
      
      // Date
      const currentDate = new Date().toLocaleDateString("en-US");
      doc.text(currentDate, 20, pageHeight - 8, { align: "left" });
    }

    // Save the PDF
    const sanitizedFileName = fileName.replace(/[^a-z0-9_-]/gi, "_");
    doc.save(`${sanitizedFileName}.pdf`);

    return true;
  } catch (error) {
    
    throw new Error("فشل في إنشاء ملف PDF");
  }
};

/**
 * Generate and share PDF (for future implementation with Web Share API)
 * @param {Object} summaryData - The summary data
 * @param {string} fileName - Optional custom filename
 */
export const shareZatounaPDF = async (summaryData, fileName = "zatouna-summary") => {
  try {
    await generateZatounaPDF(summaryData, fileName);
    
    // Future: Implement Web Share API if supported
    if (navigator.share) {
      // This would require converting to blob first
    }
    
    return true;
  } catch (error) {
    throw error;
  }
};

/**
 * Generic PDF export function for backward compatibility with SoftGrid
 * @param {Array} data - Array of data objects to export
 * @param {Array} columns - Column definitions
 * @param {string} fileName - Optional filename
 */
export const exportToPdf = (data, columns, fileName = "export") => {
  try {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    // Define colors
    const colors = {
      primary: [120, 105, 211],
      white: [255, 255, 255],
      text: [30, 41, 59],
    };

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Add header
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 20, "F");

    doc.setTextColor(...colors.white);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("EMt7any Educational Platform", pageWidth / 2, 12, { align: "center" });

    // Prepare table data
    const headers = columns.map(col => col.header || col.field || "");
    const tableData = data.map(row => 
      columns.map(col => {
        const value = row[col.field];
        return value !== null && value !== undefined ? String(value) : "";
      })
    );

    // Add table
    jsPDFAutoTable(doc, {
      startY: 25,
      head: [headers],
      body: tableData,
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 3,
        halign: "right",
      },
      headStyles: {
        fillColor: colors.primary,
        textColor: colors.white,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: {
        fillColor: [250, 250, 255],
      },
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      doc.setFillColor(...colors.primary);
      doc.rect(0, pageHeight - 10, pageWidth, 10, "F");

      doc.setTextColor(...colors.white);
      doc.setFontSize(8);
      const currentDate = new Date().toLocaleDateString("en-US");
      doc.text(currentDate, 10, pageHeight - 4);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 10, pageHeight - 4, { align: "right" });
    }

    // Save the PDF
    const sanitizedFileName = fileName.replace(/[^a-z0-9_-]/gi, "_");
    doc.save(`${sanitizedFileName}.pdf`);

    return true;
  } catch (error) {
    
    throw new Error("فشل في تصدير البيانات");
  }
};
